import { useApolloClient } from "@apollo/client";
import { assertNever, getDataOrNull } from "@msys/common";
import { DataGrid, GridColDef } from "@msys/ui";
import { Button, Link } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useParams } from "react-router-dom";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../../commons/layout/Page.js";
import { PageContainer } from "../../../commons/layout/PageContainer.js";
import { DebugAllPropsButton } from "../../../features/doc-items/debug/PropertiesDebug.js";
import { ShowTemplateVariantProtocolButton } from "../../../features/templates/quote/buttons/ShowTemplateVariantProtocolButton.js";
import {
  buildDocPath,
  fromVersionNumberParamToVersionNumberInt,
} from "../../../utils.js";
import {
  TemplateQuoteVersionTroubleshoot_CompileDocIsolatedExpressionResultDiagnosticFragment,
  useTemplateQuoteVersionTroubleshootQuery,
} from "./TemplateQuoteVersionTroubleshoot.generated.js";

interface Props {
  docId: string;
  submenuItems: PageTopbarItem[];
  prefixBreadcrumbs: BreadcrumbItem[];
  pageSubHeader: React.ReactElement | null;
  pathToDocList: string;
}

export function TemplateQuoteVersionTroubleshoot({
  docId,
  submenuItems,
  prefixBreadcrumbs,
  pageSubHeader,
  pathToDocList,
}: Props) {
  const { versionNumber: versionNumberFromParam } = useParams();
  const versionNumber = fromVersionNumberParamToVersionNumberInt(
    versionNumberFromParam
  );
  if (!versionNumber) throw new Error("Version number missing");

  const { t } = useTranslate(["Global", "Templates"]);

  const client = useApolloClient();
  const query = useTemplateQuoteVersionTroubleshootQuery({
    client,
    variables: { id: docId, versionNumber, applyOverrides: false },
    notifyOnNetworkStatusChange: true,
  });
  const quoteTemplate = getDataOrNull(
    query.data?.quoteTemplateVersion
  )?.quoteTemplate;
  const expressionValidationResults = getDataOrNull(
    query.data?.compileDocIsolatedExpression
  )?.results;

  const columns = React.useMemo(
    (): GridColDef<TemplateQuoteVersionTroubleshoot_CompileDocIsolatedExpressionResultDiagnosticFragment>[] => [
      {
        field: "title",
        headerName: t("Item", {
          ns: "Global",
        }),
        flex: 1,
        renderCell: ({ row }) => (
          <Link
            href={`${buildDocPath(
              `${pathToDocList}/${row.docId}`,
              row.versionNumber ?? null
            )}${row.versionNumber ? "" : "/edit"}/items/${row.itemId}`}
            target="_blank"
            onClick={event => {
              event.stopPropagation();
            }}
          >{`${row.itemPdfPath} ${row.itemTitle}`}</Link>
        ),
      },
      {
        field: "id",
        headerName: t("Property", {
          ns: "Global",
        }),
        flex: 1,
        valueGetter: (value, row) => {
          if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticComputedProp"
          ) {
            return row.key;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticAttribute"
          ) {
            return row.attribute;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticPropertyMapping"
          ) {
            return row.toKey;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticRecommendedEligibleIf"
          ) {
            return "n/a";
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticRecommendedIncludeIf"
          ) {
            return "n/a";
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticProductSearchFilterExpression"
          ) {
            return row.propertyKey;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticAskIf"
          ) {
            return row.key;
          } else {
            assertNever(row);
          }
        },
      },
      {
        field: "expression",
        headerName: t("Formula", {
          ns: "Global",
        }),
        flex: 2,
        renderCell: ({ row }) => {
          if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticComputedProp"
          ) {
            return row.expr;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticAttribute"
          ) {
            return row.expr;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticPropertyMapping"
          ) {
            return row.fromExpr;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticRecommendedEligibleIf"
          ) {
            return row.expr;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticRecommendedIncludeIf"
          ) {
            return row.expr;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticProductSearchFilterExpression"
          ) {
            return row.expr;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticAskIf"
          ) {
            return row.askIfExpr;
          } else {
            assertNever(row);
          }
        },
      },
      {
        field: "messageText",
        headerName: t("Error", {
          ns: "Global",
        }),
        flex: 2,
        valueGetter: (value, row) => row.messageText,
      },
    ],
    [pathToDocList, t]
  );

  return (
    <Page
      title={quoteTemplate?.title ?? ""}
      subtitle={t("Document Templates", { ns: "Templates" })}
      submenuItems={submenuItems}
      breadcrumbs={prefixBreadcrumbs}
      subHeader={pageSubHeader}
      headerType="template"
      action={
        quoteTemplate && [
          <DebugAllPropsButton
            key="debug-all-props"
            projectId={null}
            docId={docId}
          />,
          <RestrictedByOrganisationPermissionWithDebug permission="template_document_update">
            <ShowTemplateVariantProtocolButton
              key="show-template-variant-protocol-checks"
              templateId={docId}
              versionNumber={versionNumber}
              pathToDocList={pathToDocList}
              type="button"
            />
          </RestrictedByOrganisationPermissionWithDebug>,
          <Button
            key="rerun-checks"
            onClick={() => {
              query.refetch();
            }}
            loading={query.loading}
            variant="contained"
          >
            {t("Re-run checks", {
              ns: "Global",
            })}
          </Button>,
        ]
      }
    >
      <PageContainer>
        <DataGrid<TemplateQuoteVersionTroubleshoot_CompileDocIsolatedExpressionResultDiagnosticFragment>
          columns={columns}
          rows={expressionValidationResults ?? []}
          getRowId={row =>
            `${row.itemId}-${row.__typename}-${JSON.stringify(row)}`
          }
          paginationMode="client"
          loading={query.loading}
        />
      </PageContainer>
    </Page>
  );
}
